import type { ReactNode } from "react";
import { invoiceReminderOffsetValues, type components } from "@dolfin/api-admin";
import { AlertTriangleIcon } from "lucide-react";
import { InvoicePdfTemplateSettings } from "@/components/InvoicePdfTemplateSettings";
import { invoiceReminderOffsetLabels } from "@/components/InvoiceRemindersTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatCountryName, formatCurrencyName } from "@/lib/referenceData";

type OrgArSettings = components["schemas"]["OrganisationArSettingsResponse"];
type CountrySummary = components["schemas"]["CountryBankProfileSummary"];
type Currency = components["schemas"]["CurrencyResponse"];

interface OrganisationArSettingsSectionProps {
  organisationId: string;
  settings: OrgArSettings | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onSaved: (settings: OrgArSettings) => void;
  countries: CountrySummary[];
  currencies: Currency[];
}

// Shown as stored, no platform defaults resolved (null terms reads "Not set", not "30 days"): staff need what the org configured.
export function OrganisationArSettingsSection({
  organisationId,
  settings,
  loading,
  error,
  onRetry,
  onSaved,
  countries,
  currencies,
}: OrganisationArSettingsSectionProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangleIcon className="size-4 shrink-0" />
            {error ?? "Could not load AR settings."}
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const bank = settings.bankDetails ?? {};

  return (
    <div className="space-y-4">
      <InvoicePdfTemplateSettings
        key={organisationId}
        organisationId={organisationId}
        settings={settings}
        onSaved={onSaved}
      />

      <Card>
        <CardHeader>
          <CardTitle>AR Settings</CardTitle>
          <CardDescription>
            The organisation's accounts-receivable defaults, as stored. New invoices start from
            these and may override them. Read-only here; the organisation edits them in its own
            settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Group title="Invoice Templates">
            <Field label="Email Template">{settings.invoiceEmailTemplate}</Field>
          </Group>

          <Group title="Payment">
            <Field label="Payment Terms">
              {settings.defaultPaymentTermsDays == null
                ? null
                : settings.defaultPaymentTermsDays === 0
                  ? "Due on receipt"
                  : `${settings.defaultPaymentTermsDays} day${settings.defaultPaymentTermsDays === 1 ? "" : "s"}`}
            </Field>
            <Field label="Late Payment Fee">
              {settings.latePaymentFee == null ? null : `${settings.latePaymentFee}%`}
            </Field>
          </Group>

          <Group title="Messaging">
            <Field label="Tone">{settings.defaultInvoiceMessageTone}</Field>
            <Field label="Reminder Schedule">
              {settings.defaultReminderOffsets.length === 0 ? (
                <span className="text-muted-foreground">None (automatic reminders off)</span>
              ) : (
                // Ordered by the canonical earliest-first enum order, not the stored order.
                invoiceReminderOffsetValues
                  .filter((offset) => settings.defaultReminderOffsets.includes(offset))
                  .map((offset) => invoiceReminderOffsetLabels[offset])
                  .join(", ")
              )}
            </Field>
            <Field label="Reply-To">{settings.defaultReplyToEmail}</Field>
            <Field label="CC" wide>
              {settings.defaultInvoiceCcEmails.length === 0 ? null : (
                <ul className="space-y-0.5">
                  {settings.defaultInvoiceCcEmails.map((email) => (
                    <li key={email} className="break-all">
                      {email}
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          </Group>

          <Group title="Invoice Numbering">
            <NumberingFields
              prefix={settings.invoiceNumberPrefix}
              nextValue={settings.invoiceNumberNextValue}
              padding={settings.invoiceNumberPadding}
            />
          </Group>

          <Group title="Credit Note Numbering">
            <NumberingFields
              prefix={settings.creditNoteNumberPrefix}
              nextValue={settings.creditNoteNumberNextValue}
              padding={settings.creditNoteNumberPadding}
            />
          </Group>

          <Group title="Bank Details">
            <Field label="Account Holder">{bank.accountHolderName}</Field>
            <Field label="Country">{formatCountryName(bank.country, countries)}</Field>
            <Field label="Currency">{formatCurrencyName(bank.currency, currencies)}</Field>
            <Field label="Sort Code" mono>
              {bank.sortCode}
            </Field>
            <Field label="Routing Number" mono>
              {bank.routingNumber}
            </Field>
            <Field label="Account Number" mono>
              {bank.accountNumber}
            </Field>
            <Field label="IBAN" mono>
              {bank.iban}
            </Field>
            <Field label="BIC/SWIFT" mono>
              {bank.bicSwift}
            </Field>
          </Group>
        </CardContent>
      </Card>
    </div>
  );
}

function NumberingFields({
  prefix,
  nextValue,
  padding,
}: {
  prefix: string;
  // Int64/Int32 on the wire, which the generated schema types as `number | string`.
  nextValue: number | string;
  padding: number | string;
}) {
  return (
    <>
      <Field label="Prefix" mono>
        {prefix}
      </Field>
      <Field label="Next Value" mono>
        {String(nextValue)}
      </Field>
      <Field label="Padding" mono>
        {String(padding)}
      </Field>
      <Field label="Next Number" mono>
        {formatNextNumber(prefix, nextValue, padding)}
      </Field>
    </>
  );
}

// Mirrors OrganisationArSettingsService.Render on the backend: "{prefix}-{value zero-padded to
// padding}", e.g. INV + 42 + 4 → INV-0042.
function formatNextNumber(prefix: string, nextValue: number | string, padding: number | string): string {
  return `${prefix}-${String(nextValue).padStart(Math.max(1, Number(padding)), "0")}`;
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-2 sm:grid-cols-4 text-sm">{children}</dl>
    </div>
  );
}

function Field({
  label,
  mono = false,
  wide = false,
  children,
}: {
  label: string;
  mono?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  const empty = children == null || children === "";
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono && !empty ? "font-mono" : undefined}>
        {empty ? <span className="text-muted-foreground">Not set</span> : children}
      </dd>
    </div>
  );
}
